import { Component, input, output, ChangeDetectionStrategy, signal, computed, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterModule } from '@angular/router';
import { IconComponent } from '@program-suite/util-icons';
import { ClickOutsideDirective } from '../../directive/click-outside.directive';



export interface MenuItem {
  name: string;
  type?: 'link' | 'button';
  link?: string | null;
  disabled?: boolean;
  action?: () => any;
}

@Component({
  selector: 'lib-button',
  imports: [CommonModule, RouterLink, IconComponent, RouterModule, ClickOutsideDirective],
  templateUrl: './button.component.html',
  styleUrl: './button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class ButtonComponent {
  /**
   * Optional selector for the grid container. If provided, dropup logic will use this container instead of the viewport.
   */
  gridContainerSelector = input<string | null>(null);
  @ViewChild('dropdownButton', { static: false }) dropdownButtonRef!: ElementRef<HTMLButtonElement>;
  @ViewChild('dropdownMenu', { static: false }) dropdownMenuRef!: ElementRef<HTMLUListElement>;

  localIsDropUp = signal(false);
  menuItems = input<MenuItem[] | null>(null);
  cssType = input<'btn-primary' | 'btn-secondary' | 'btn-link' | 'btn-icon' | 'btn-close'| 'btn-dropdown' | 'btn-icon-dropdown'>('btn-primary');
  text = input<string>();
  buttonType = input<'button' | 'link'>('button');
  type = input<'button' | 'submit' | 'reset'>('button')
  closeType = input<'modal' | 'notification' | 'snackbar'>('modal');
  size = input<'small' | 'small-link' | 'medium' | 'large' | 'full'>('large');
  width = input<'full' | null>(null);
  bold = input<boolean | null>(null);
  externalLink = input<boolean | null>(null);
  href = input<string | null>(null);
  routerLink = input<string | null>(null);
  icon = input<string>('');
  disabled = input<boolean>()
  dropdownDisabled = input<boolean>(false);
  showDropdown = false;
  mainClick = output<void>();
  isTemporarilyDisabled = signal(false);
  temporarilyDisabledSeconds = 1.5
  forceDropUp = input<boolean>(false);
  disabledComputed = computed(() => {
    return this.disabled() || this.isTemporarilyDisabled();
  });

  ngOnInit() {
    if ((this.cssType() === 'btn-primary'  || this.cssType() === 'btn-secondary'  ||  this.cssType() === 'btn-link'  ||  this.cssType() === 'btn-dropdown' ) && !this.text()) {
      console.error('[ButtonComponent] Text is required when cssType is "btn-primary, btn-secondary, btn-link, btn-dropdown.');
    }


    if (this.cssType() === 'btn-dropdown' && !this.menuItems()) {
      console.error('[ButtonComponent] menuItems is required when cssType is "btn-dropdown".');
    }
  }

  isBold() {
    switch (this.bold()) {
      case true:
        return 'bold';
      default:
        return '';
    }
  }

  getSizeClass() {
    switch (this.size()) {
      case 'small-link':
        return 'small';
      case 'small':
        return 'btn-small';
      case 'medium':
        return 'medium';
        case 'large':
        return 'btn-large';
      default:
        return 'btn-large';
    }
  }

  getWidthClass() {
    switch (this.width()) {
      case 'full':
        return 'btn-full';
      default:
        return '';
    }
  }

  isDisabled(){
    switch (true) {
      case this.disabled():
        return 'disabled-btn';
      default:
        return '';
    }
  }

  onItemClick(item: MenuItem) {
    this.showDropdown = false;
    item.action?.();
  }

  toggleDropdown() {
    this.showDropdown = !this.showDropdown;
    if (this.showDropdown) {
      this.measureDropDirection();
    }
  }

  /**
   * Measures the available space below and above the button, and sets localIsDropUp accordingly.
   */
  measureDropDirection() {
    if (this.forceDropUp && this.forceDropUp()) {
      this.localIsDropUp.set(true);
      return;
    }
    if (!this.dropdownButtonRef || !this.dropdownButtonRef.nativeElement) return;
    const buttonRect = this.dropdownButtonRef.nativeElement.getBoundingClientRect();
    const dropdownHeight = this.dropdownMenuRef?.nativeElement?.offsetHeight || 115; // fallback to 115px

    // Try to use the grid container if selector is provided
    let spaceBelow: number, spaceAbove: number;
    const selector = this.gridContainerSelector() || undefined;
    if (selector && typeof window !== 'undefined') {
      const gridEl = document.querySelector(selector);
      if (gridEl) {
        const gridRect = gridEl.getBoundingClientRect();
        spaceBelow = gridRect.bottom - buttonRect.bottom;
        spaceAbove = buttonRect.top - gridRect.top;
      } else {
        // fallback to viewport
        const viewportHeight = window.innerHeight;
        spaceBelow = viewportHeight - buttonRect.bottom;
        spaceAbove = buttonRect.top;
      }
    } else {
      // fallback to viewport
      const viewportHeight = window.innerHeight;
      spaceBelow = viewportHeight - buttonRect.bottom;
      spaceAbove = buttonRect.top;
    }
    // If not enough space below, but enough above, open upwards
    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      this.localIsDropUp.set(true);
    } else {
      this.localIsDropUp.set(false);
    }
  }

  /**
   * Returns the dropdown menu style for icon-dropdown, supporting dropup if isDropUp is true.
   */
  getDropdownMenuStyle() {
    const isDropUp = this.localIsDropUp();
    // Always use absolute positioning so the dropdown stays attached to the button, even when the grid scrolls
    if (this.cssType() === 'btn-icon-dropdown') {
      if (isDropUp) {
        return {
          position: 'absolute',
          bottom: '100%', // show above the button
          top: 'auto',
          width: 'auto',
          minWidth: '100%',
          zIndex: 9999
        };
      } else {
        return {
          position: 'absolute',
          top: '100%', // show below the button
          bottom: 'auto',
          width: 'auto',
          minWidth: '100%',
          zIndex: 9999
        };
      }
    }
    // Default dropdown (dropdown below)
    return {
      width: 'auto',
      top: '20px',
      position: 'absolute',
      zIndex: 9999
    };
  }

  close() {
    if (this.closeType() === 'modal') {
      this.mainClick.emit();
    } else if (this.closeType() === 'notification') {
      this.mainClick.emit();
    } else if (this.closeType() === 'snackbar') {
      this.mainClick.emit();
    }
  }

  temporarilyDisable() {
    this.isTemporarilyDisabled.set(true);
    setTimeout(() => {
      this.isTemporarilyDisabled.set(false);
    }, (this.temporarilyDisabledSeconds * 1000));
  }
}
